import './stopProgress.scss';
import * as React from 'react';
import { Stack, Box, Grid, Typography } from '@mui/material';
import { useTranslation } from '@chr/online-shared-ui-localizations';
import { IsoToDate, IsoToDateAtTime, FormatLocation, ConvertStopTypes } from '../../utilities/display-utilities';
import { Stop, StopType } from '../../apis/dtos';
import { ModeCode } from "../../constants/modeCode";

export interface StopProgressProps {
  stop: Stop;
  isMobileScreen: boolean;
  cargoReady?: string;
  shipmentModeCode: string;
}

export function StopProgress({ stop, isMobileScreen, cargoReady, shipmentModeCode }: StopProgressProps) {
  const translate = useTranslation();
  const stopType: string = ConvertStopTypes(stop.stopType);
  const isGfsAirOrOcean = shipmentModeCode === ModeCode.GfsAir || shipmentModeCode === ModeCode.GfsOcean;
  const displayDatesWithTime = !isGfsAirOrOcean;

  interface InformationDisplayProps {
    label: React.ReactNode;
    value: React.ReactNode;
  }

  function InformationDisplay({ label, value }: InformationDisplayProps) {
      return (
        <Stack direction={isMobileScreen ? 'row' : 'column'} justifyContent={isMobileScreen ? 'space-between' : 'flex-start'} spacing={isMobileScreen ? 2 : 0} width='100%'>
          <Typography component="div" variant='body1'>{label}</Typography>
          <Typography component="div" variant="subtitle2" textAlign={isMobileScreen ? 'right' : 'inherit'}>{value}</Typography>
        </Stack>
      )
  }

  function DisplayDate(value?:string){
    return displayDatesWithTime ? IsoToDateAtTime(value, translate) : IsoToDate(value);
  }

  return (
    <>
      <Grid
        container
        paddingY={2}
        paddingX={3}
        sx={ {borderLeft: (theme) => isMobileScreen ? '0 none transparent' : `1px solid ${theme.palette.grey[200]}`}}
        className="stop-data-container"
        data-testid={`stop-progress-${stop.sequenceNumber}`}
      >
        <Grid item className="stop-detail" xs={12} md data-testid={`stop-location-${stop.sequenceNumber}`}>
          <InformationDisplay
            label={translate(stopType)}
            value={FormatLocation(stop.location)}
          />
        </Grid>
        {
          stop.stopType === StopType.Pick
          && cargoReady?.trim()
          && isGfsAirOrOcean
          &&
          <Grid item className="stop-detail" xs={12} md>
            <InformationDisplay
              label={translate('Cargo Ready')}
              value={DisplayDate(cargoReady)}
            />
          </Grid>
        }
        {
          (stop.stopType == StopType.Pick || stop.stopType === StopType.Drop)
          &&
          <Grid item className="stop-detail" xs={12} md>
            <InformationDisplay
              label={translate('Requested')}
              value={DisplayDate(stop.requested)}
            />
          </Grid>
        }
        <Grid item className="stop-detail" xs={12} md={2}>
          <InformationDisplay
            label={translate('Scheduled')}
            value={
              <>
                <Box>{DisplayDate(stop.scheduledOpenDateTime)} -</Box>
                <Box>{DisplayDate(stop.scheduledCloseDateTime)}</Box>
              </>
            }
          />
        </Grid>
        <Grid item className="stop-detail" xs={12} md data-testid={`stop-eta-${stop.sequenceNumber}`}>
          <InformationDisplay
            label={translate('Estimated Arrival')}
            value={
              <>
                <Box>{DisplayDate(stop.eta)}</Box>
                {stop.eta && stop.etaSource && <Typography component="div" variant='body1'>{translate(stop.etaSource)}</Typography>}
              </>
            }
          />
        </Grid>
        <Grid item className="stop-detail" xs={12} md>
          <InformationDisplay
            label={translate('Arrived')}
            value={DisplayDate(stop.arrived)}
          />
        </Grid>
        <Grid item className="stop-detail" xs={12} md>
          <InformationDisplay
            label={translate('Departed')}
            value={DisplayDate(stop.departed)}
          />
        </Grid>
      </Grid>
    </>
  );
}
